import React from 'react';
import { Player } from './types';
import { useSpecializations } from './hooks/useSpecializations';
import { useLocalStorage } from './hooks/useLocalStorage';
import { PlayerTable } from './components/PlayerTable';
import './App.css';

function App() {
  const { specializations, loading, error, getSpecializationsForLevel } = useSpecializations();
  const [players, setPlayers] = useLocalStorage<Player[]>('once-human-players', []);

  const generatePlayerId = () => {
    return `player-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handlePlayerAdd = () => {
    const newPlayer: Player = {
      id: generatePlayerId(),
      name: `Player ${players.length + 1}`,
      choices: {}
    };
    setPlayers([...players, newPlayer]);
  };

  const handlePlayerUpdate = (playerId: string, updatedPlayer: Player) => {
    setPlayers(players.map(player => 
      player.id === playerId ? updatedPlayer : player
    ));
  };

  const handlePlayerDelete = (playerId: string) => {
    setPlayers(players.filter(player => player.id !== playerId));
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify({ players, timestamp: new Date().toISOString() }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `once-human-tracker-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.players && Array.isArray(data.players)) {
          setPlayers(data.players);
        }
      } catch (error) {
        alert('Error importing data: Invalid file format');
      }
    };
    reader.readAsText(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading specializations...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-600 text-xl mb-4">Error loading data</div>
          <div className="text-gray-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto p-4">
        {/* Header with Import/Export */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Once Human Specialization Tracker</h1>
            <p className="text-gray-600 mt-1">
              Track player specialization choices across all levels
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleExportData}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Export Data
            </button>
            <label className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 cursor-pointer">
              Import Data
              <input
                type="file"
                accept=".json"
                onChange={handleImportData}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Main Table */}
        <PlayerTable
          players={players}
          specializations={specializations}
          onPlayerUpdate={handlePlayerUpdate}
          onPlayerAdd={handlePlayerAdd}
          onPlayerDelete={handlePlayerDelete}
          getSpecializationsForLevel={getSpecializationsForLevel}
        />

        {/* Instructions */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3">How to use:</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Add players using the "Add Player" button</li>
            <li>For each player, click on a level cell to select their 4 offered specializations</li>
            <li>Mark which specialization they actually chose with the radio button</li>
            <li>Use color coding to quickly identify specialization categories</li>
            <li>Export/import data to save your progress or share with others</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default App;
